import React from 'react';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { closingInTrustTypeOptions, stateOptions, suffixOptions } from 'constants/options';

import { LoanClosingTestIds } from '../LoanClosing.testIds';

export const titleHolderEditConfigFields = {
  firstName: {
    fieldId: 'firstName',
    type: FieldType.INPUT,
    nameInModel: 'firstName',
    inputProps: {
      placeholder: 'Type first name...',
      label: 'First Name',
      disabled: false,
      required: true,
      variant: 'outlined',
      dataTestId: LoanClosingTestIds.TITLE_HOLDER_INFO_FORM_FIRST_NAME_INPUT,
      'data-test-value': 'first-name',
    },
  },
  middleName: {
    fieldId: 'middleName',
    type: FieldType.INPUT,
    nameInModel: 'middleName',
    inputProps: {
      placeholder: 'Type middle name...',
      label: 'Middle Name',
      disabled: false,
      required: false,
      variant: 'outlined',
      dataTestId: LoanClosingTestIds.TITLE_HOLDER_INFO_FORM_MIDDLE_NAME_INPUT,
      'data-test-value': 'middle-name',
    },
  },
  lastName: {
    fieldId: 'lastName',
    type: FieldType.INPUT,
    nameInModel: 'lastName',
    inputProps: {
      placeholder: 'Type last name...',
      label: 'Last Name',
      disabled: false,
      required: true,
      variant: 'outlined',
      dataTestId: LoanClosingTestIds.TITLE_HOLDER_INFO_FORM_LAST_NAME_INPUT,
      'data-test-value': 'last-name',
    },
  },
  suffix: {
    fieldId: 'suffix',
    type: FieldType.SELECT,
    nameInModel: 'suffix',
    inputProps: {
      placeholder: 'Type suffix...',
      label: 'Suffix',
      disabled: false,
      required: true,
      variant: 'outlined',
      options: suffixOptions,
      dataTestId: LoanClosingTestIds.TITLE_HOLDER_INFO_FORM_SUFFIX_SELECT,
      'data-test-value': suffixOptions[0].value,
    },
  },
  email: {
    fieldId: 'email',
    type: FieldType.INPUT,
    nameInModel: 'email',
    inputProps: {
      placeholder: 'Type email...',
      label: 'Email',
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.EMAIL,
      dataTestId: LoanClosingTestIds.TITLE_HOLDER_INFO_FORM_EMAIL_INPUT,
      'data-test-value': 'email@email.com',
    },
  },
  cellPhoneNumber: {
    fieldId: 'cellPhoneNumber',
    type: FieldType.INPUT,
    nameInModel: 'cellPhoneNumber',
    inputProps: {
      placeholder: 'Type Cell Phone Number...',
      label: 'Cell Phone Number',
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEL,
      dataTestId: LoanClosingTestIds.TITLE_HOLDER_INFO_FORM_CELL_PHONE_NUMBER_INPUT,
      'data-test-value': '731-222-2222',
    },
  },
  isTrusteeOfTrust: {
    fieldId: 'isTrusteeOfTrust',
    type: FieldType.CHECKBOX,
    nameInModel: 'isTrusteeOfTrust',
    inputProps: {
      label: 'Is Trustee of Trust',
      required: false,
      variant: 'outlined',
    },
  },
};

export const titleHolderPropertyRelatedInfoEditConfigFields = {
  bplCompanyName: {
    fieldId: 'bplCompanyName',
    type: FieldType.INPUT,
    nameInModel: 'bplCompanyName',
    inputProps: {
      placeholder: 'Type Company Name...',
      label: 'Company Name',
      disabled: false,
      required: true,
      variant: 'outlined',
      dataTestId: LoanClosingTestIds.TITLE_HOLDER_INFO_FORM_COMPANY_NAME_INPUT,
      className: 'w-256',
    },
  },
  mannerHeld: {
    fieldId: 'mannerHeld',
    type: FieldType.SELECT,
    nameInModel: 'mannerHeld',
    inputProps: {
      disableClearable: true,
      disablePortal: true,
      placeholder: 'Type Title Held As...',
      label: 'Title Held As',
      disabled: false,
      required: true,
      variant: 'outlined',
      options: [],
      dataTestId: LoanClosingTestIds.TITLE_HOLDER_INFO_FORM_MANNER_HELD_SELECT,
      className: 'manner-held-wrapper',
    },
  },
  vestingDescription: {
    fieldId: 'vestingDescription',
    type: FieldType.INPUT,
    nameInModel: 'vestingDescription',
    inputProps: {
      label: 'Vesting Description',
      disabled: false,
      variant: 'outlined',
      dataTestId: LoanClosingTestIds.TITLE_HOLDER_INFO_FORM_VESTING_DESCRIPTION_INPUT,
      className: 'w-400',
      rows: 10,
      maxLength: Number.MAX_SAFE_INTEGER,
      multiline: true,
    },
  },
  vestingTypeId: {
    fieldId: 'vestingTypeId',
    type: FieldType.SELECT,
    nameInModel: 'vestingTypeId',
    inputProps: {
      placeholder: 'Type Borrower Vesting...',
      label: 'Borrower Vesting',
      disabled: false,
      required: false,
      variant: 'outlined',
      options: [],
      dataTestId: LoanClosingTestIds.TITLE_HOLDER_INFO_FORM_BORROWER_VESTING_SELECT,
    },
  },
};

export const trustInfoEditConfigFields = {
  type: {
    fieldId: 'type',
    type: FieldType.SELECT,
    nameInModel: 'type',
    inputProps: {
      options: closingInTrustTypeOptions,
      placeholder: 'Closing in Trust Type',
      label: 'Closing in Trust Type',
      required: true,
      variant: 'outlined',
    },
  },
  establishedDate: {
    fieldId: 'establishedDate',
    type: FieldType.DATE,
    nameInModel: 'establishedDate',
    inputProps: {
      label: 'Trust Established Date',
      required: true,
      variant: 'outlined',
      format: COMMON_DATE_FORMAT,
    },
  },
  name: {
    fieldId: 'name',
    type: FieldType.INPUT,
    nameInModel: 'name',
    inputProps: {
      placeholder: 'Trust Name',
      label: 'Trust Name',
      maxLength: 300,
      required: true,
      variant: 'outlined',
    },
  },
  establishedState: {
    fieldId: 'establishedState',
    type: FieldType.SELECT,
    nameInModel: 'establishedState',
    inputProps: {
      options: stateOptions,
      placeholder: 'Trust Established State',
      label: 'Trust Established State',
      required: true,
      variant: 'outlined',
    },
  },
  trustee: {
    fieldId: 'trustee',
    type: FieldType.INPUT,
    nameInModel: 'trustee',
    inputProps: {
      placeholder: 'Trustee',
      label: 'Trustee',
      maxLength: 300,
      required: false,
      widthInColumns: 3,
      variant: 'outlined',
    },
  },
  settlor: {
    fieldId: 'settlor',
    type: FieldType.INPUT,
    nameInModel: 'settlor',
    inputProps: {
      placeholder: 'Settlor',
      label: 'Settlor',
      maxLength: 300,
      required: false,
      widthInColumns: 3,
      variant: 'outlined',
    },
  },
  beneficiary: {
    fieldId: 'beneficiary',
    type: FieldType.INPUT,
    nameInModel: 'beneficiary',
    inputProps: {
      placeholder: 'Beneficiary',
      label: 'Beneficiary',
      required: false,
      maxLength: 300,
      widthInColumns: 3,
      variant: 'outlined',
    },
  },
  isOwner: {
    fieldId: 'isOwner',
    type: FieldType.CHECKBOX,
    nameInModel: 'isOwner',
    inputProps: {
      label: 'Is Owner',
      required: false,
      widthInColumns: 3,
      variant: 'outlined',
    },
  },
};

export const titleHolderEditConfig = {
  groups: [
    {
      type: FieldType.GROUP,
      fields: [
        titleHolderEditConfigFields.firstName,
        titleHolderEditConfigFields.middleName,
        titleHolderEditConfigFields.lastName,
        titleHolderEditConfigFields.suffix,
        titleHolderEditConfigFields.email,
        titleHolderEditConfigFields.cellPhoneNumber,
        titleHolderEditConfigFields.isTrusteeOfTrust,
      ],
    },
  ],
};
